import { useMemo } from 'react';
import type { WatchQueryFetchPolicy } from '@apollo/client';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useEventInfo } from '@shared/utils/eventInfo';
import { classifyDraftRow } from '@apps/admin/message/shared/draftRowClassification';
import { useMessagingDraftCountsByEventQuery } from '@apps/admin/message/shared/MessagingDraftCounts.graphql.generated';

export type MessagingDraftCounts = Readonly<{
  draftCount: number;
  scheduledCount: number;
  failedScheduledCount: number;
}>;

/**
 * Batch counts for the Messaging section, derived from the same `messageDraftsByEvent` rows and
 * `classifyDraftRow` rules as the Messaging tabs. Backed by a lean count-only query (id / batchId /
 * status / scheduledAt) so it can run from globally-mounted surfaces (the dashboard product row,
 * the universal burger menu) without pulling the full Drafts-list payload.
 *
 * Gated on `messageDraftsEnabled`; scheduled/failed-scheduled buckets fold into drafts when the
 * scheduling flag is off (see `classifyDraftRow`).
 *
 * `eventId` (when provided) takes precedence over the active event. `fetchPolicy` defaults to
 * `cache-and-network` for visible surfaces (the Messaging tabs) that want fresh counts; the
 * globally-mounted dashboard/burger path passes `cache-first` to avoid an unconditional background
 * refetch on every admin page load for pills that aren't visible until the menu is opened.
 */
export const useMessagingDraftCounts = (options?: { eventId?: string; fetchPolicy?: WatchQueryFetchPolicy }): MessagingDraftCounts => {
  const [messageDraftsEnabled] = useIsFeatureEnabled('messageDraftsEnabled');
  const [messageSchedulingEnabled] = useIsFeatureEnabled('messageSchedulingEnabled');
  const { eventInfo } = useEventInfo();
  const eventId = options?.eventId ?? eventInfo?.eventId ?? '';

  const { data } = useMessagingDraftCountsByEventQuery({
    variables: { eventId },
    skip: !eventId || !messageDraftsEnabled,
    fetchPolicy: options?.fetchPolicy ?? 'cache-and-network',
    batchMode: 'fast',
  });

  return useMemo(() => {
    const rows = data?.messageDraftsByEvent ?? [];
    const draftBatches = new Set<string>();
    const scheduledBatches = new Set<string>();
    const failedScheduledBatches = new Set<string>();
    for (const row of rows) {
      const bucket = classifyDraftRow(row, { schedulingFlagEnabled: messageSchedulingEnabled });
      if (bucket === 'draft') draftBatches.add(row.messageBatchId);
      else if (bucket === 'scheduled') scheduledBatches.add(row.messageBatchId);
      else if (bucket === 'failed-scheduled') failedScheduledBatches.add(row.messageBatchId);
    }
    return {
      draftCount: draftBatches.size,
      scheduledCount: scheduledBatches.size,
      failedScheduledCount: failedScheduledBatches.size,
    };
  }, [data, messageSchedulingEnabled]);
};
